import { Component, computed, OnInit, signal } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { AuthService } from 'src/app/auth/services/auth.service';
import { AccountPageService } from 'src/app/features/user-account/services/account-page.service';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ButtonsRowComponent } from 'src/app/features/shared/controls/container/buttons-row/buttons-row.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { SlideToggleComponent } from 'src/app/features/shared/controls/container/slide-toggle/slide-toggle.component';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { claimName } from 'src/assets/common/enums/claim-name.g';
import { pageName } from 'src/assets/common/enums/page-name.g';

// 'status' = aan/uit tonen; 'enrolling' = QR + secret + code-invoer (Inschakelen);
// 'confirming' = wachtwoord-invoer ter bevestiging (Uitschakelen).
type TTwoFactorViewState = 'status' | 'enrolling' | 'confirming';

@Component({
    selector: 'app-two-factor-setup',
    templateUrl: './two-factor-setup.component.html',
    styleUrls: ['./two-factor-setup.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        InputControlComponent,
        ButtonActionComponent,
        ButtonsRowComponent,
        SlideToggleComponent,
    ],
})
export class TwoFactorSetupComponent implements OnInit {
    private readonly __formBuilder = injectRequired(UntypedFormBuilder);
    private readonly __accountPageService = injectRequired(AccountPageService);
    private readonly __authService = injectRequired(AuthService);
    private readonly __applicationUserService = injectRequired(ApplicationUserService);

    // In-/uitschakelen alleen met Settings › TwoFactor_Update (app-niveau claim, geen route-scope).
    // De backend weigert enroll/confirm/disable zonder die claim; de status blijft zichtbaar.
    canUpdate$$ = computed(() => this.__applicationUserService.claim_check(
        pageName.settings, null, claimName.twoFactor_Update, this.__applicationUserService.userClaims$$()));

    private __viewState$$ = signal<TTwoFactorViewState>('status');
    viewState$$ = this.__viewState$$.asReadonly();

    private __enabled$$ = signal<boolean>(false);
    enabled$$ = this.__enabled$$.asReadonly();

    // De toggle toont de status en start de stappen; hij staat vast zonder recht en tijdens een stap.
    // Een stap wijzigt enabled$$ pas na bevestiging, dus de toggle springt bij annuleren vanzelf terug.
    toggleDisabled$$ = computed(() => !this.canUpdate$$() || this.viewState$$() !== 'status');

    toggleLabel$$ = computed(() => this.enabled$$() ? 'ingeschakeld' : 'uitgeschakeld');

    private __qrCodeDataUrl$$ = signal<string | null>(null);
    qrCodeDataUrl$$ = this.__qrCodeDataUrl$$.asReadonly();
    private __secret$$ = signal<string | null>(null);
    secret$$ = this.__secret$$.asReadonly();
    // Bestaande sleutel hergebruikt: geen QR (het account in de app werkt al); de QR komt pas via
    // "nieuwe sleutel".
    private __keyExisting$$ = signal<boolean>(false);
    keyExisting$$ = this.__keyExisting$$.asReadonly();

    showErrors = false;

    private __enrollForm: UntypedFormGroup = this.__formBuilder.group({
        code: ['', Validators.required],
    });
    get EnrollForm() { return this.__enrollForm; }
    get EnrollFormControls() { return this.__enrollForm.controls as { [key: string]: FormControl }; }

    private __disableForm: UntypedFormGroup = this.__formBuilder.group({
        password: ['', Validators.required],
    });
    get DisableForm() { return this.__disableForm; }
    get DisableFormControls() { return this.__disableForm.controls as { [key: string]: FormControl }; }

    ngOnInit(): void {
        this.__status_load();
    }

    emit_enabled_change(enabled: boolean): void {
        if (enabled) {
            this.emit_enroll();
        } else {
            this.emit_startDisable();
        }
    }

    // reset: nieuwe sleutel → het oude account in de authenticator-app werkt niet meer.
    emit_enroll(reset: boolean = false): void {
        this.__authService.postTwoFactorEnroll$(reset)
            .subscribe({
                next: resp => {
                    this.__keyExisting$$.set(!!resp?.keyExisting);
                    this.__secret$$.set(resp?.secret ?? null);
                    this.__qrCodeDataUrl$$.set(resp?.qrCodePngBase64 ? `data:image/png;base64,${resp.qrCodePngBase64}` : null);
                    this.showErrors = false;
                    this.__enrollForm.reset();
                    this.__viewState$$.set('enrolling');
                },
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    emit_confirmEnroll(): void {
        if (this.__enrollForm.invalid) {
            this.showErrors = true;
            return;
        }

        const code: string = this.EnrollFormControls.code.value;

        this.__authService.postTwoFactorConfirm$(code)
            .subscribe({
                next: () => {
                    this.__enabled$$.set(true);
                    this.__enrollment_reset();
                    this.__viewState$$.set('status');
                    // AdminPanel kan eerder op TwoFactorRequired gestrand zijn: opnieuw proben.
                    this.__applicationUserService.isAdminPanel_refresh();
                },
                // Ongeldige code: fout wordt centraal gerapporteerd, formulier blijft op deze stap staan.
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    emit_cancelEnroll(): void {
        this.__enrollment_reset();
        this.__viewState$$.set('status');
    }

    emit_startDisable(): void {
        this.showErrors = false;
        this.__disableForm.reset();
        this.__viewState$$.set('confirming');
    }

    emit_confirmDisable(): void {
        if (this.__disableForm.invalid) {
            this.showErrors = true;
            return;
        }

        const password: string = this.DisableFormControls.password.value;

        this.__authService.postTwoFactorDisable$(password)
            .subscribe({
                // De backend heeft alle sessies beëindigd (refresh-tokens ingetrokken): ook deze
                // sessie uitloggen, opnieuw inloggen gaat zonder 2FA-stap.
                next: () => this.__accountPageService.logOut(),
                // Verkeerd wachtwoord: fout wordt centraal gerapporteerd, formulier blijft staan.
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    emit_cancelDisable(): void {
        this.__disableForm.reset();
        this.__viewState$$.set('status');
    }

    private __status_load(): void {
        this.__authService.getTwoFactorStatus$()
            .subscribe({
                next: resp => this.__enabled$$.set(!!resp?.enabled),
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    private __enrollment_reset(): void {
        this.__qrCodeDataUrl$$.set(null);
        this.__secret$$.set(null);
        this.__keyExisting$$.set(false);
        this.__enrollForm.reset();
    }
}
